import { useMemo } from 'react';
import { useQuickSearchAssetResultV2Fragment } from '../gql/generated/QuickSearchAssetResultV2';
import { useQuickSearchAssetResultV2NlpFragment } from '../gql/generated/QuickSearchAssetResultV2Nlp';
import type { SectionId } from '../types';

type UseQuickSearchAssetDataProps = {
  assetId: string;
  sectionId?: SectionId;
};

/**
 * Hook to fetch asset data and the appropriate matches based on the section.
 *
 * This hook handles fetching both lexical and NLP matches from the cache and
 * automatically selects the correct matches based on the sectionId:
 * - If sectionId is 'nlp', returns NLP matches
 * - If sectionId is 'lexical' or undefined, returns lexical matches
 *
 * @param props.assetId - The ID of the asset to fetch
 * @param props.sectionId - The section ID ('lexical', 'nlp', or undefined)
 * @returns Object containing asset data and the appropriate matches
 */
export const useQuickSearchAssetData = ({
  assetId,
  sectionId,
}: UseQuickSearchAssetDataProps) => {
  // Fetch lexical matches (standard search results)
  const {
    data: { result: lexicalAssetData, matches: lexicalMatches },
  } = useQuickSearchAssetResultV2Fragment({
    from: { __typename: 'QuickSearchAssetResult', id: assetId },
  });

  // Fetch NLP matches (AI-powered search results)
  const {
    data: { nlpMatches },
  } = useQuickSearchAssetResultV2NlpFragment({
    from: { __typename: 'QuickSearchAssetResult', id: assetId },
  });

  // Determine which matches to use based on the section
  // In SINGLE mode (sectionId is undefined), always use lexical matches
  const { assetData, matches } = useMemo(() => {
    const isNlpSection = sectionId === 'nlp';

    return {
      assetData: lexicalAssetData,
      matches: isNlpSection ? nlpMatches : lexicalMatches,
    };
  }, [lexicalAssetData, lexicalMatches, nlpMatches, sectionId]);

  return {
    assetData,
    matches,
  };
};
